import { useCallback, useEffect, useRef, useState } from "react";

import {
  abandonEmailLink,
  completeSignInFromLink,
  isReturningFromEmailLink,
  rememberedAddress,
} from "./email-link";

/**
 * What the person should do after a failed completion.
 *
 * - `retry`: the same link can still work. A cold auth blocking function is
 *   the usual cause: GCIP gives it 7 s and then refuses the sign-in as
 *   unavailable, without spending the code.
 * - `confirm-address`: the address typed on another device did not match.
 * - `new-link`: the code is spent or expired, so only a fresh mail helps.
 */
export type CompletionAction = "retry" | "confirm-address" | "new-link";

export type EmailLinkCompletion =
  | { phase: "none" }
  | { phase: "needs-address" }
  | { phase: "pending" }
  | { phase: "failed"; message: string; action: CompletionAction };

/**
 * Longest the page waits on `signInWithEmailLink` before offering a retry.
 *
 * GCIP answers inside about 8 s even when a blocking function times out, so
 * reaching this means the network, not the backend, is stuck.
 */
export const COMPLETION_TIMEOUT_MS = 25_000;

class CompletionTimeout extends Error {
  readonly code = "connect/sign-in-timeout";
}

function authErrorCode(cause: unknown): string | null {
  const code = (cause as { code?: unknown } | null)?.code;
  if (typeof code === "string") return code;
  const message = cause instanceof Error ? cause.message : String(cause);
  return /\b(auth\/[a-z-]+)/.exec(message)?.[1] ?? null;
}

const TRY_AGAIN =
  "Signing in took longer than it should. This usually works on a second try.";

/**
 * Turns a completion failure into what the person reads and can do.
 *
 * Known codes get a sentence. Anything else is shown verbatim, because the
 * blocking functions' refusal ("restricted to approved organisation
 * accounts") arrives inside the message and is the only explanation the
 * person gets.
 */
export function describeCompletionError(cause: unknown): {
  message: string;
  action: CompletionAction;
} {
  const code = authErrorCode(cause);
  switch (code) {
    case "connect/sign-in-timeout":
    case "auth/the-service-is-currently-unavailable":
    case "auth/network-request-failed":
    case "auth/timeout":
      return { message: TRY_AGAIN, action: "retry" };
    case "auth/too-many-requests":
      return {
        message: "Too many sign-in attempts. Wait a minute, then try again.",
        action: "retry",
      };
    case "auth/invalid-action-code":
    case "auth/expired-action-code":
      return {
        message:
          "This sign-in link has expired or has already been used. Request a new one.",
        action: "new-link",
      };
    case "auth/invalid-email":
      return {
        message:
          "That address does not match the one this link was sent to. Check it and try again.",
        action: "confirm-address",
      };
    default:
      return {
        message: cause instanceof Error ? cause.message : String(cause),
        action: "retry",
      };
  }
}

function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new CompletionTimeout()), ms);
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Completes a returning email link and reports where that stands.
 *
 * Owned by the sign-in screen rather than by the form inside it, so the
 * screen can replace itself with a full-page "Signing you in" state. Before,
 * the form stayed on screen with its buttons greyed out for the 5-15 s a cold
 * start took, and people wondered whether to click elsewhere.
 *
 * Success leaves the phase at "pending" on purpose. The auth state listener
 * swaps the screen out once Firebase reports the user, and falling back to
 * the form in between would flash the controls the person just left.
 */
export function useEmailLinkCompletion(): {
  completion: EmailLinkCompletion;
  confirmAddress: (email: string) => void;
  retry: () => void;
  startOver: () => void;
} {
  const [completion, setCompletion] = useState<EmailLinkCompletion>(() =>
    isReturningFromEmailLink()
      ? rememberedAddress()
        ? { phase: "pending" }
        : { phase: "needs-address" }
      : { phase: "none" },
  );
  const address = useRef<string | null>(null);
  // An oobCode is single-use, so the automatic completion runs at most once
  // per mount. StrictMode double-invokes effects in development, and the
  // second pass would otherwise fail on a code the first one spent.
  const started = useRef(false);

  const attempt = useCallback(async (email: string) => {
    address.current = email;
    setCompletion({ phase: "pending" });
    try {
      await withTimeout(completeSignInFromLink(email), COMPLETION_TIMEOUT_MS);
    } catch (cause) {
      setCompletion({ phase: "failed", ...describeCompletionError(cause) });
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!isReturningFromEmailLink()) return;
    const remembered = rememberedAddress();
    // Same browser: finish without asking again. A different one is asked
    // for the address, which the link deliberately does not carry.
    if (remembered) void attempt(remembered);
  }, [attempt]);

  const confirmAddress = useCallback(
    (email: string) => void attempt(email),
    [attempt],
  );

  const retry = useCallback(() => {
    if (completion.phase === "failed" && completion.action === "retry") {
      if (address.current) void attempt(address.current);
      return;
    }
    if (
      completion.phase === "failed" &&
      completion.action === "confirm-address"
    ) {
      setCompletion({ phase: "needs-address" });
    }
  }, [attempt, completion]);

  const startOver = useCallback(() => {
    abandonEmailLink();
    setCompletion({ phase: "none" });
  }, []);

  return { completion, confirmAddress, retry, startOver };
}
